import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { agency, navGroups } from '@/mocks/portal';
import { useRole } from '@/context/RoleContext';
import { useBrand } from '@/context/BrandContext';
import type { NavItem } from '@/types/portal';

interface SidebarProps {
  onNavigate?: () => void;
}

/** Delay between menu items sliding in when the sidebar first appears. */
const SLIDE_STEP_MS = 30;

function SidebarLink({
  item,
  order,
  onNavigate,
}: {
  item: NavItem;
  /** Position in the whole menu, for the slide-in stagger. */
  order: number;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      style={{ animationDelay: `${order * SLIDE_STEP_MS}ms` }}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-label transition-colors cursor-pointer animate-slide-in-left ${
          isActive
            ? 'text-background-50 font-semibold'
            : 'text-foreground-200 hover:bg-background-50/10 hover:text-background-50 before:absolute before:left-0 before:top-1/2 before:h-0 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:bg-primary-400 before:transition-all before:duration-200 hover:before:h-4'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`w-5 h-5 flex items-center justify-center text-lg transition-transform duration-200 ${
              isActive ? 'animate-pop' : 'group-hover:translate-x-0.5 group-hover:scale-110 motion-reduce:transform-none'
            }`}
          >
            <i className={item.icon} />
          </span>
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ onNavigate }: SidebarProps) {
  const { can, role } = useRole();
  const { profile, logos } = useBrand();
  const sidebarMark = logos.avatar || logos.mark;

  const limitSet = role.approvalsCeiling !== 'Not set';
  const roleCardHint = !limitSet
    ? `Your role is ${role.name}. Approval limits (the largest payment a role can approve on its own) aren't available yet.`
    : role.approvalsCeiling === 'None'
      ? `Your role is ${role.name}. It can't approve payments.`
      : role.approvalsCeiling === 'Unlimited'
        ? `Your role is ${role.name}. It can approve payments of any amount.`
        : `Your role is ${role.name}. It can approve payments up to ${role.approvalsCeiling} on its own; larger ones need a second approver.`;
  const roleCard = (
    <>
      <span className="w-8 h-8 flex shrink-0 items-center justify-center rounded-full bg-primary-700 text-background-50 ring-1 ring-primary-500/60">
        <i className={`${role.name === 'Admin' ? 'ri-admin-fill' : 'ri-shield-user-fill'} text-base`} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-foreground-400">Signed in as</p>
        <p className="text-xs font-medium text-background-50 truncate">{role.name}</p>
        <p className="text-[11px] text-foreground-400 truncate">
          Approval limit: {limitSet ? role.approvalsCeiling : 'not set yet'}
        </p>
      </div>
    </>
  );

  const groups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.permission || can(item.permission),
      ),
    }))
    .filter((group) => group.items.length > 0);

  let order = 0;
  // One burgundy highlight glides to whichever page is open.
  const { pathname } = useLocation();
  const highlight = useSlidingIndicator<HTMLElement>('a[aria-current="page"]', `${pathname}|${groups.length}`);

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-foreground-950 border-r border-foreground-900">
      {/* A soft brand glow behind the agency name */}
      <div
        className="pointer-events-none absolute -left-16 -top-20 h-56 w-56 rounded-full bg-primary-500/20 blur-3xl"
        aria-hidden="true"
      />
      <Link
        to="/dashboard"
        onClick={onNavigate}
        title="Go to Dashboard"
        className="group relative mx-3 my-3 flex items-center gap-3 rounded-lg px-2 py-2 transition-colors cursor-pointer hover:bg-background-50/10"
      >
        <span className="w-10 h-10 shrink-0 flex items-center justify-center overflow-hidden rounded-lg bg-background-50 shadow-md shadow-black/30 transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105 motion-reduce:transform-none">
          {sidebarMark ? (
            <img
              src={sidebarMark}
              decoding="async"
              alt={`${profile.name || agency.name} logo`}
              className="h-full w-full object-contain"
            />
          ) : (
            <img
              src="/apple-touch-icon.png"
              decoding="async"
              alt="Twiva logo"
              className="h-full w-full object-contain"
            />
          )}
        </span>
        <div className="min-w-0">
          <p className="font-heading text-base font-bold leading-tight text-background-50 truncate">
            {profile.name || agency.name}
          </p>
          <p className="text-[11px] font-semibold text-foreground-300 truncate">
            powered by {agency.poweredBy}
          </p>
        </div>
      </Link>

      <nav ref={highlight.ref} className="relative flex-1 overflow-y-auto px-3 pb-4">
        {highlight.style ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 rounded-md bg-primary-500 shadow-[0_6px_18px_-6px] shadow-primary-500/70"
            style={highlight.style}
          >
            {/* A thin light edge on the left, like a lit tab. */}
            <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-background-50/70" />
          </span>
        ) : null}
        {groups.map((group) => (
          <div key={group.label} className="mb-2.5">
            <p
              className="px-3 pb-1 pt-1 text-[10px] font-label font-semibold uppercase tracking-overline text-foreground-400 animate-slide-in-left"
              style={{ animationDelay: `${order * SLIDE_STEP_MS}ms` }}
            >
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <SidebarLink key={item.to} item={item} order={++order} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="relative border-t border-foreground-900 px-4 py-4">
        {can('roles.manage') ? (
          <NavLink
            to="/settings/roles"
            onClick={onNavigate}
            title={`${roleCardHint} Click to change it in Roles & Permissions.`}
            className="flex items-center gap-2 -mx-2 rounded-md px-2 py-1.5 transition-colors cursor-pointer hover:bg-background-50/10"
          >
            {roleCard}
          </NavLink>
        ) : (
          <div title={roleCardHint} className="flex items-center gap-2">
            {roleCard}
          </div>
        )}
      </div>
    </div>
  );
}